"use server";

import { prisma } from "@/lib/prisma";
import { requireUserId } from "@/lib/action-guards";
import { materializeDueReminders } from "@/lib/escalation-reminders";
import { z } from "zod";
import { defineAction } from "@/lib/define-action";
import { sweepResolved } from "@/lib/notification-actions";
import { NOTIFICATION_EVENTS, categoryOfType } from "@/lib/notification-events";
import type { NotificationCategory } from "@/lib/notification-categories";
import { setEmailPreference } from "@/lib/notification-preferences";

export type NotificationDTO = {
  id: number;
  type: string;
  message: string;
  link: string | null;
  read: boolean;
  createdAt: Date;
  // Rich data: absent on older rows, which only have `message`.
  title: string | null;
  context: string | null;
  actionLabel: string | null;
  /** Set = action required (stays until the work is done). */
  actionKey: string | null;
  resolvedAt: Date | null;
  resolvedByName: string | null;
  snoozedUntil: Date | null;
  category: NotificationCategory | null;
};

export type InboxTab = "all" | "action" | "unread" | "snoozed";
export type Inbox = { items: NotificationDTO[]; actionCount: number; unreadCount: number; hasMore: boolean };

const SELECT = {
  id: true, type: true, message: true, link: true, read: true, createdAt: true, title: true, context: true, actionLabel: true,
  actionKey: true, resolvedAt: true, resolvedByName: true, snoozedUntil: true,
} as const;

const inboxInput = z.object({
  tab: z.enum(["all", "action", "unread", "snoozed"]).default("all"),
  category: z.string().max(32).optional(),
  take: z.number().int().min(1).max(200).default(20),
});

/**
 * The signed-in user's inbox. Open action items whose work was finished elsewhere are closed first,
 * so every recipient sees "Resolved by" on their next load or poll.
 */
export const getInbox = defineAction({
  guard: "user",
  input: inboxInput,
  run: async ({ userId }, opts: z.input<typeof inboxInput>): Promise<Inbox> => {
    const { tab, category, take } = inboxInput.parse(opts);
    materializeDueReminders().catch((err) => console.error("Failed to materialize due RCA reminders:", err));
    await sweepResolved(userId).catch((err) => console.error("Failed to sweep resolved notifications:", err));
    const now = new Date();
    const awake = { OR: [{ snoozedUntil: null }, { snoozedUntil: { lte: now } }] };
    const open = { actionKey: { not: null }, resolvedAt: null };
    const types = category && category in NOTIFICATION_EVENTS ? NOTIFICATION_EVENTS[category as NotificationCategory].map((e) => e.type) : null;
    const where = {
      userId,
      ...(types ? { type: { in: types } } : {}),
      ...(tab === "action" ? { ...open, ...awake } : tab === "snoozed" ? { ...open, snoozedUntil: { gt: now } } : tab === "unread" ? { read: false, resolvedAt: null } : {}),
    };
    const [rows, actionCount, unreadCount] = await Promise.all([
      prisma.notification.findMany({ where, orderBy: { createdAt: "desc" }, take: take + 1, select: SELECT }),
      prisma.notification.count({ where: { userId, ...open, ...awake } }),
      prisma.notification.count({ where: { userId, read: false, actionKey: null } }),
    ]);
    return {
      items: rows.slice(0, take).map((n) => ({ ...n, category: categoryOfType(n.type) })),
      actionCount,
      unreadCount,
      hasMore: rows.length > take,
    };
  },
});

const snoozeInput = z.object({ id: z.number().int().positive(), days: z.number().int().min(0).max(30) });

/** Hide an open action item for `days` days (0 wakes it now). Action items cannot be dismissed. */
export const snoozeNotification = defineAction({
  guard: "user",
  input: snoozeInput,
  run: async ({ userId }, input: z.input<typeof snoozeInput>) => {
    const { id, days } = snoozeInput.parse(input);
    const snoozedUntil = days === 0 ? null : new Date(Date.now() + days * 86_400_000);
    const r = await prisma.notification.updateMany({ where: { id, userId, actionKey: { not: null }, resolvedAt: null }, data: { snoozedUntil } });
    return { ok: r.count > 0 };
  },
});

export async function markAllNotificationsRead(): Promise<{ count: number }> {
  const userId = await requireUserId();
  const result = await prisma.notification.updateMany({
    where: { userId, read: false, actionKey: null },
    data: { read: true },
  });
  return { count: result.count };
}

export async function markNotificationRead(id: number): Promise<{ ok: boolean }> {
  const userId = await requireUserId();
  const result = await prisma.notification.updateMany({
    where: { id, userId },
    data: { read: true },
  });
  return { ok: result.count > 0 };
}

export async function updateNotificationPreference(category: NotificationCategory, enabled: boolean): Promise<void> {
  await setEmailPreference(category, enabled);
}
